import { t } from '@/utils/i18nHelper'
import { translateApiMessage } from '@/utils/apiMessageMap'

const SYNC_BELL_TITLE_MAP = {
  'Sincronização concluída': 'servers.sync.bellCompleted',
  'Sincronização concluída com ressalvas': 'servers.sync.bellPartial',
  'Sincronização com erros': 'servers.sync.bellWithErrors',
  'Erro na sincronização': 'servers.sync.bellError'
}

/**
 * Sucesso se a API respondeu para pelo menos uma conta; erro só se todas falharem ou job falhou.
 * @param {Record<string, unknown>} result
 * @param {'completed'|'failed'|string} [state]
 */
export function resolveSyncNotificationPresentation(result = {}, state = 'completed') {
  if (state === 'failed') {
    return {
      titleKey: 'servers.sync.bellError',
      type: 'error',
      toastTitleKey: 'servers.sync.errorTitle',
      toastType: 'error'
    }
  }

  const synced = Number(result.synced ?? 0)
  const partial = Number(result.partial ?? 0)
  const failed = Number(result.failed ?? 0)
  const eligible = Number(result.eligible ?? synced + partial + failed)
  const sshApiFail = Number(result.ssh_api_fail ?? 0)
  const v2rayApiFail = Number(result.v2ray_api_fail ?? 0)

  if (eligible > 0 && failed >= eligible && synced === 0 && partial === 0) {
    return {
      titleKey: 'servers.sync.bellWithErrors',
      type: 'error',
      toastTitleKey: 'servers.sync.completedWithErrorsTitle',
      toastType: 'error'
    }
  }

  if (partial > 0 || failed > 0 || sshApiFail > 0 || v2rayApiFail > 0) {
    return {
      titleKey: 'servers.sync.bellPartial',
      type: 'warning',
      toastTitleKey: 'servers.sync.completedPartialTitle',
      toastType: 'warning'
    }
  }

  return {
    titleKey: 'servers.sync.bellCompleted',
    type: 'success',
    toastTitleKey: 'servers.sync.completedTitle',
    toastType: 'success'
  }
}

function summarizeApiTotals(result = {}) {
  const sshApiOk = Number(result.ssh_api_ok ?? result.ssh_sent ?? 0)
  const v2rayApiOk = Number(result.v2ray_api_ok ?? result.v2ray_sent ?? 0)
  const sshApiFail = Number(result.ssh_api_fail ?? 0)
  const v2rayApiFail = Number(result.v2ray_api_fail ?? 0)

  return {
    ok: sshApiOk + v2rayApiOk,
    fail: sshApiFail + v2rayApiFail,
    sshOk: sshApiOk,
    v2rayOk: v2rayApiOk
  }
}

/**
 * Mensagem multilinha para toasts (contagens).
 * @param {Record<string, unknown>} result
 */
export function buildSyncResultMessage(result = {}) {
  const parts = []
  const synced = Number(result.synced ?? 0)
  const partial = Number(result.partial ?? 0)
  const failed = Number(result.failed ?? 0)
  const eligible = Number(result.eligible ?? synced + partial + failed)
  const sshSent = Number(result.ssh_sent ?? 0)
  const v2raySent = Number(result.v2ray_sent ?? 0)
  const api = summarizeApiTotals(result)

  if (eligible > 0) {
    parts.push(t('servers.sync.resultAccountsProcessed', { count: eligible }))
  } else {
    parts.push(t('servers.sync.resultNoneEligible'))
    return parts.join('\n')
  }

  const protocolParts = []
  if (sshSent > 0) protocolParts.push(t('servers.sync.resultSshSent', { count: sshSent }))
  if (v2raySent > 0) protocolParts.push(t('servers.sync.resultV2raySent', { count: v2raySent }))
  if (protocolParts.length > 0) {
    parts.push(protocolParts.join(' · '))
  }

  const outcomeParts = []
  if (api.ok > 0) {
    outcomeParts.push(t('servers.sync.summaryApiOkTotal', {
      count: api.ok,
      ssh: api.sshOk,
      v2ray: api.v2rayOk
    }))
  }
  if (api.fail > 0) {
    outcomeParts.push(t('servers.sync.summaryApiRejectedTotal', { count: api.fail }))
  }
  if (outcomeParts.length > 0) {
    parts.push(t('servers.sync.summaryOutcome', { list: outcomeParts.join(', ') }))
  }

  if (synced > 0) parts.push(t('servers.sync.resultAccountsSynced', { count: synced }))
  if (partial > 0) parts.push(t('servers.sync.resultPartialOk', { count: partial }))
  if (failed > 0) parts.push(t('servers.sync.resultAccountsFailed', { count: failed }))

  return parts.join('\n')
}

/**
 * Resumo numa linha (sininho / SSE).
 * @param {Record<string, unknown>} result
 */
export function buildSyncSummaryInline(result = {}) {
  const parts = []
  const eligible = Number(result.eligible ?? 0)
  const sshSent = Number(result.ssh_sent ?? 0)
  const v2raySent = Number(result.v2ray_sent ?? 0)
  const api = summarizeApiTotals(result)

  if (eligible > 0) {
    parts.push(t('servers.sync.summaryAccountsProcessed', { count: eligible }))
  } else {
    return t('servers.sync.resultNoneEligible')
  }

  const protocolParts = []
  if (sshSent > 0) protocolParts.push(t('servers.sync.summarySshSent', { count: sshSent }))
  if (v2raySent > 0) protocolParts.push(t('servers.sync.summaryV2raySent', { count: v2raySent }))
  if (protocolParts.length > 0) {
    parts.push(protocolParts.join(', '))
  }

  const outcomeParts = []
  if (api.ok > 0) {
    outcomeParts.push(t('servers.sync.summaryApiOkTotal', {
      count: api.ok,
      ssh: api.sshOk,
      v2ray: api.v2rayOk
    }))
  }
  if (api.fail > 0) {
    outcomeParts.push(t('servers.sync.summaryApiRejectedTotal', { count: api.fail }))
  }
  if (outcomeParts.length > 0) {
    parts.push(t('servers.sync.summaryOutcome', { list: outcomeParts.join(', ') }))
  }

  return parts.join(' · ')
}

/**
 * Localiza notificação do admin (SSE), incluindo sync de servidor.
 * @param {{ title?: string, message?: string, type?: string, data?: Record<string, unknown> }} notif
 */
export function localizeAdminNotification(notif) {
  if (!notif || typeof notif !== 'object') {
    return notif
  }

  const data = notif.data
  if (data?.kind === 'server_sync') {
    const serverName = String(data.server_name ?? '')
    const state = data.state
    const result = data.result

    if (state === 'completed' && result && typeof result === 'object') {
      const presentation = resolveSyncNotificationPresentation(result, 'completed')
      return {
        title: t(presentation.titleKey),
        message: t('servers.sync.notificationBody', {
          server: serverName,
          summary: buildSyncSummaryInline(result)
        }),
        type: presentation.type,
        data
      }
    }

    if (state === 'failed') {
      let errorText = String(notif.message ?? '')
      errorText = errorText.replace(/^Servidor «[^»]+»:\s*/u, '')
      const translated = translateApiMessage(errorText) || errorText

      return {
        title: t('servers.sync.bellError'),
        message: t('servers.sync.notificationFailed', {
          server: serverName,
          error: translated || t('servers.sync.errorGeneric')
        }),
        type: 'error',
        data
      }
    }
  }

  const titleKey = SYNC_BELL_TITLE_MAP[notif.title]
  return {
    ...notif,
    title: titleKey ? t(titleKey) : notif.title,
    message: translateApiMessage(notif.message) ?? notif.message
  }
}
